<template>
  <div v-if="(cardios && cardios.length > 0) || edit">
    <h2 class="display-6 mb-4 d-flex align-items-center gap-2">
      <font-awesome-icon icon="fa-solid fa-heart-pulse" />
      {{ $t('cardio.title') }}
    </h2>

    <div class="card  p-2">
      <!-- Desktop -->
      <div class="table-responsive d-none d-md-block">
        <table class="table table-borderless table-sm align-middle text-center">
          <thead>
            <tr>
              <th>#</th>
              <th><font-awesome-icon icon="fa-solid fa-person-swimming" /> {{ $t('cardio.activity') }}</th>
              <th><font-awesome-icon icon="fa-solid fa-gauge-high" /> {{ $t('cardio.speed') }}</th>
              <th><font-awesome-icon icon="fa-solid fa-wave-square" /> {{ $t('cardio.heart') }}</th>
              <th><font-awesome-icon icon="fa-solid fa-infinity" /> {{ $t('cardio.distance') }}</th>
              <th><font-awesome-icon icon="fa-solid fa-stopwatch-20" /> {{ $t('cardio.time') }}</th>
              <th><font-awesome-icon icon="fa-solid fa-fire" /> {{ $t('cardio.calories') }}</th>
              <th v-if="edit" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="(cardio, index) in cardios" :key="index">
              <td v-if="!edit">{{ index + 1 }}</td>
              <td v-else>
                <button type="button" class="btn btn-sm btn-outline-danger" @click="removeCardio(index)">
                  <font-awesome-icon icon="fa-solid fa-trash" />
                </button>
              </td>
              <td>
                <input
                  v-if="edit"
                  v-model="cardio.type"
                  type="text"
                  maxlength="150"
                  class="form-control"
                  :class="{ 'is-invalid': cardio.errors?.type }"
                  :placeholder="$t('cardio.activity')"
                  required
                  @input="updateValue" />
                <span v-else>{{ cardio.type }}</span>
              </td>
              <td>
                <input
                  v-if="edit"
                  v-model.number="cardio.speed"
                  class="form-control"
                  type="number"
                  inputmode="decimal"
                  min="0"
                  :class="{ 'is-invalid': cardio.errors?.speed }"
                  :placeholder="$t('cardio.speed')"
                  required
                  @input="updateValue" />
                <span v-else class="font-monospace">{{ cardio.speed }}</span>
              </td>
              <td>
                <input
                  v-if="edit"
                  v-model.number="cardio.heart"
                  class="form-control"
                  type="number"
                  inputmode="numeric"
                  min="60"
                  max="220"
                  :class="{ 'is-invalid': cardio.errors?.heart }"
                  :placeholder="$t('cardio.heart')"
                  required
                  @input="updateValue" />
                <span v-else class="font-monospace">{{ cardio.heart }}</span>
              </td>
              <td>
                <input
                  v-if="edit"
                  v-model.number="cardio.distance"
                  type="number"
                  inputmode="decimal"
                  min="0"
                  class="form-control"
                  :class="{ 'is-invalid': cardio.errors?.distance }"
                  :placeholder="$t('cardio.distance')"
                  required
                  @input="updateValue" />
                <span v-else class="font-monospace">{{ cardio.distance }}</span>
              </td>
              <td>
                <input
                  v-if="edit"
                  v-model.number="cardio.time"
                  class="form-control"
                  type="number"
                  inputmode="decimal"
                  min="0"
                  :class="{ 'is-invalid': cardio.errors?.time }"
                  :placeholder="$t('cardio.time')"
                  required
                  @input="updateValue" />
                <span v-else class="font-monospace">{{ cardio.time }}</span>
              </td>
              <td>
                <input
                  v-if="edit"
                  v-model.number="cardio.calories"
                  class="form-control"
                  type="number"
                  inputmode="numeric"
                  min="0"
                  :class="{ 'is-invalid': cardio.errors?.calories }"
                  :placeholder="$t('cardio.calories')"
                  required
                  @input="updateValue" />
                <span v-else class="font-monospace">{{ cardio.calories }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile -->
      <div class="list-group d-md-none border-0">
        <div v-for="(cardio, index) in cardios" :key="index" class="">

          <!-- Activity / Type -->
          <div class="list-group-item d-flex py-2 bg-dark-subtle">
            <div class="d-flex align-items-center w-100 gap-2">
              <template v-if="edit">
                <input
                  v-model="cardio.type"
                  type="text"
                  maxlength="150"
                  class="form-control"
                  :class="{ 'is-invalid': cardio.errors?.type }"
                  :placeholder="$t('cardio.activity')"
                  @input="updateValue" />
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  aria-label="Remove"
                  @click="removeCardio(index)">
                  <font-awesome-icon icon="fa-solid fa-trash" />
                </button>
              </template>
              <template v-else>
                <span class="text-dark lead">{{ cardio.type || '—' }}</span>
              </template>
            </div>
          </div>

          <!-- Speed -->
          <div class="list-group-item d-flex justify-content-between align-items-center py-2">
            <div class="d-flex align-items-center gap-2">
              <font-awesome-icon icon="fa-solid fa-gauge-high" />
              <span class="small">{{ $t('cardio.speed') }}</span>
            </div>
            <template v-if="edit">
              <input
                v-model.number="cardio.speed"
                type="number"
                inputmode="decimal"
                min="0"
                class="form-control text-end"
                style="width: 80px"
                :class="{ 'is-invalid': cardio.errors?.speed }"
                @input="updateValue" />
            </template>
            <template v-else>
              <span class="font-monospace">{{ cardio.speed }}</span>
            </template>
          </div>

          <!-- Heart -->
          <div class="list-group-item d-flex justify-content-between align-items-center py-2">
            <div class="d-flex align-items-center gap-2">
              <font-awesome-icon icon="fa-solid fa-wave-square" />
              <span class="small">{{ $t('cardio.heart') }}</span>
            </div>
            <template v-if="edit">
              <input
                v-model.number="cardio.heart"
                type="number"
                inputmode="numeric"
                min="60"
                max="220"
                class="form-control text-end"
                style="width: 80px"
                :class="{ 'is-invalid': cardio.errors?.heart }"
                @input="updateValue" />
            </template>
            <template v-else>
              <span class="font-monospace">{{ cardio.heart }}</span>
            </template>
          </div>

          <!-- Distance -->
          <div class="list-group-item d-flex justify-content-between align-items-center py-2">
            <div class="d-flex align-items-center gap-2">
              <font-awesome-icon icon="fa-solid fa-infinity" />
              <span class="small">{{ $t('cardio.distance') }}</span>
            </div>
            <template v-if="edit">
              <input
                v-model.number="cardio.distance"
                type="number"
                inputmode="decimal"
                min="0"
                class="form-control text-end"
                style="width: 80px"
                :class="{ 'is-invalid': cardio.errors?.distance }"
                @input="updateValue" />
            </template>
            <template v-else>
              <span class="font-monospace">{{ cardio.distance }}</span>
            </template>
          </div>

          <!-- Time -->
          <div class="list-group-item d-flex justify-content-between align-items-center py-2">
            <div class="d-flex align-items-center gap-2">
              <font-awesome-icon icon="fa-solid fa-stopwatch-20" />
              <span class="small">{{ $t('cardio.time') }}</span>
            </div>
            <template v-if="edit">
              <input
                v-model.number="cardio.time"
                class="form-control text-end"
                type="number"
                inputmode="decimal"
                min="0"
                style="width: 80px"
                :class="{ 'is-invalid': cardio.errors?.time }"
                @input="updateValue" />
            </template>
            <template v-else>
              <span class="font-monospace">{{ cardio.time }}</span>
            </template>
          </div>

          <!-- Calories -->
          <div class="list-group-item d-flex justify-content-between align-items-center py-2">
            <div class="d-flex align-items-center gap-2">
              <font-awesome-icon icon="fa-solid fa-fire" />
              <span class="small">{{ $t('cardio.calories') }}</span>
            </div>
            <template v-if="edit">
              <input
                v-model.number="cardio.calories"
                type="number"
                inputmode="numeric"
                min="0"
                class="form-control text-end"
                style="width: 80px"
                :class="{ 'is-invalid': cardio.errors?.calories }"
                @input="updateValue" />
            </template>
            <template v-else>
              <span class="font-monospace">{{ cardio.calories }}</span>
            </template>
          </div>
        </div>
      </div>
      <div v-if="edit" class="p-2 text-center">
        <button type="button" class="btn btn-outline-dark" @click="addCardio">
          <font-awesome-icon icon="fa-solid fa-circle-plus" />
          {{ $t('cardio.new') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  edit: {
    type: Boolean,
    default: false,
  },
});

const cardios = ref(props.modelValue);
const emit = defineEmits(['update:modelValue', 'update:isValid']);

watch(() => props.modelValue, (newValue) => {
  cardios.value = newValue;
});

function addCardio() {
  if (cardios.value.length >= 10) {
    globalThis.$toast?.showToast(t('errorsMsg.cardiosLimit'), 'dark');
    return;
  }
  cardios.value.push({
    type: '',
    speed: 0,
    heart: 60,
    distance: 0,
    time: 0,
    calories: 0,
  });
}

function removeCardio(index) {
  cardios.value.splice(index, 1);
}

function validateCardios() {
  let isValid = true;
  const updated = cardios.value.map((entry) => {
    const newEntry = { ...entry, errors: {} };

    if (!newEntry.type?.trim() || newEntry.type.length < 3) {
      newEntry.errors.type = true;
      isValid = false;
    }

    if (typeof newEntry.speed !== 'number' || newEntry.speed < 0) {
      newEntry.errors.speed = true;
      isValid = false;
    }

    if (typeof newEntry.heart !== 'number' || newEntry.heart < 60 || newEntry.heart > 220) {
      newEntry.errors.heart = true;
      isValid = false;
    }

    if (typeof newEntry.distance !== 'number' || newEntry.distance < 0) {
      newEntry.errors.distance = true;
      isValid = false;
    }

    if (typeof newEntry.time !== 'number' || newEntry.time < 0) {
      newEntry.errors.time = true;
      isValid = false;
    }

    if (typeof newEntry.calories !== 'number' || newEntry.calories < 0) {
      newEntry.errors.calories = true;
      isValid = false;
    }

    return newEntry;
  });

  cardios.value = updated;
  return isValid;
}

function updateValue() {
  const valid = validateCardios();
  emit('update:modelValue', cardios.value);
  emit('update:isValid', valid);
}

</script>
